import { useEffect, useState, useCallback } from "react";
import { api, formatNumber, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, Plus, Trash2, UserCircle, Calendar as CalIcon, Clock, TrendingUp } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "personnel", label: "Personel" },
  { key: "leaves", label: "İzinler" },
  { key: "overtime", label: "Mesai" },
  { key: "report", label: "Aylık Rapor" },
];

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function PersonnelPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";
  const [tab, setTab] = useState("personnel");
  const [personnel, setPersonnel] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [overtimes, setOvertimes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(currentMonth());
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(null); // "personnel" | "leave" | "overtime"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, l, o, s] = await Promise.all([
        api.get("/personnel"),
        api.get("/personnel/leaves", { params: { month } }),
        api.get("/personnel/overtime", { params: { month } }),
        api.get("/personnel/summary", { params: { month } }),
      ]);
      setPersonnel(p.data);
      setLeaves(l.data);
      setOvertimes(o.data);
      setSummary(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="personnel-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-sky-500 mb-2">Ekip Yönetimi</div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight font-display">Personel & Mesai</h1>
          <p className="text-slate-600 mt-2">İzin takibi, mesai kaydı ve aylık raporlar.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs font-bold text-slate-600">Ay:</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="h-10 w-40 border-2 border-slate-300 font-mono-num" data-testid="month-select" />
        </div>
      </div>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="site-card p-5 rounded-lg" data-testid="stat-personnel-total">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Toplam Personel</div>
            <div className="font-display font-black text-3xl text-slate-900 mt-2 flex items-center gap-2">
              <UserCircle size={22} className="text-sky-500" />
              {personnel.length}
            </div>
          </div>
          <div className="site-card p-5 rounded-lg" data-testid="stat-ot-total">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Aylık Toplam Mesai</div>
            <div className="font-display font-black text-3xl text-green-700 mt-2 flex items-center gap-2">
              <Clock size={20} className="text-green-500" />
              {formatNumber(summary.grand_total_hours)} sa
            </div>
          </div>
          <div className="site-card p-5 rounded-lg" data-testid="stat-onleave-today">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Bugün İzinli</div>
            <div className="font-display font-black text-3xl text-amber-700 mt-2 flex items-center gap-2">
              <CalIcon size={20} className="text-amber-500" />
              {summary.on_leave_today.length}
            </div>
            {summary.on_leave_today.length > 0 && (
              <div className="text-xs text-slate-600 mt-1 truncate">
                {summary.on_leave_today.map((l) => l.personnel_name).join(", ")}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b-2 border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className={`px-4 py-2 font-bold text-sm border-b-2 -mb-0.5 ${tab === t.key ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`tab-${t.key}`}>
            {t.label}
          </button>
        ))}
        {canEdit && tab !== "report" && (
          <Button size="sm" className="ml-auto bg-sky-500 hover:bg-sky-600 text-white font-bold" onClick={() => setOpenAdd(tab)} data-testid="add-btn">
            <Plus size={14} className="mr-1" /> Ekle
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-sky-500" size={28} /></div>
      ) : (
        <div className="site-card rounded-lg overflow-hidden">
          {tab === "personnel" && <PersonnelTable rows={personnel} canEdit={canEdit} onReload={load} />}
          {tab === "leaves" && <LeavesTable rows={leaves} canEdit={canEdit} onReload={load} />}
          {tab === "overtime" && <OvertimeTable rows={overtimes} canEdit={canEdit} onReload={load} />}
          {tab === "report" && summary && <ReportTable summary={summary} personnel={personnel} />}
        </div>
      )}

      <AddDialog open={openAdd} onClose={() => setOpenAdd(null)} personnel={personnel} onSaved={() => { setOpenAdd(null); load(); }} />
    </div>
  );
}

function PersonnelTable({ rows, canEdit, onReload }) {
  const [deleteId, setDeleteId] = useState(null);
  const remove = async () => {
    try { await api.delete(`/personnel/${deleteId}`); toast.success("Silindi"); setDeleteId(null); onReload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <>
      {rows.length === 0 ? <div className="py-16 text-center text-slate-500">Henüz personel yok. Sağ üstteki Ekle ile başlayın.</div> : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-xs uppercase tracking-widest text-slate-500">
            <th className="text-left py-3 px-4 font-bold">Ad Soyad</th>
            <th className="text-left py-3 px-4 font-bold">Rol/Görev</th>
            <th className="text-left py-3 px-4 font-bold">Telefon</th>
            <th className="text-right py-3 px-4 font-bold">İşlem</th>
          </tr></thead>
          <tbody>{rows.map((p) => (
            <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`person-row-${p.id}`}>
              <td className="py-3 px-4 font-semibold text-slate-900">{p.name}</td>
              <td className="py-3 px-4 text-slate-700">{p.role}</td>
              <td className="py-3 px-4 text-slate-600 font-mono-num">{p.phone || "—"}</td>
              <td className="py-3 px-4 text-right">
                {canEdit && (
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => setDeleteId(p.id)} data-testid={`delete-person-${p.id}`}>
                    <Trash2 size={14} />
                  </Button>
                )}
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Personeli sil?</AlertDialogTitle>
            <AlertDialogDescription>Personel ve tüm izin/mesai kayıtları silinecek.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={remove}>Sil</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function LeavesTable({ rows, canEdit, onReload }) {
  const remove = async (id) => { try { await api.delete(`/personnel/leaves/${id}`); toast.success("Silindi"); onReload(); } catch (err) { toast.error(formatApiError(err)); } };
  const [selectedDate, setSelectedDate] = useState(null);

  // Compute all dates that have leave (as a Set of YYYY-MM-DD strings)
  const leaveDates = new Set();
  for (const l of rows) {
    const start = new Date(l.start_date);
    const end = new Date(l.end_date);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      leaveDates.add(d.toISOString().slice(0, 10));
    }
  }

  const modifiers = {
    onLeave: (date) => leaveDates.has(date.toISOString().slice(0, 10)),
  };
  const modifiersClassNames = {
    onLeave: "bg-amber-100 text-amber-900 font-black border-2 border-amber-400 rounded-md",
  };

  // Persons on the selected date
  const selectedKey = selectedDate ? selectedDate.toISOString().slice(0, 10) : null;
  const onSelectedDay = selectedKey ? rows.filter((l) => l.start_date <= selectedKey && l.end_date >= selectedKey) : [];

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border-2 border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="leave-calendar">
          <div className="text-xs font-black uppercase tracking-widest text-slate-700 mb-2 flex items-center gap-2">
            <CalIcon size={14} className="text-amber-500" /> İzin Takvimi
          </div>
          <p className="text-xs text-slate-500 mb-3">Sarı hücreler personelin izinli olduğu günleri gösterir. Bir güne tıklayarak o gün izinli olanları görebilirsiniz.</p>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            className="rounded-md border-0 bg-white"
          />
          {selectedKey && (
            <div className="mt-3 p-3 bg-white border border-amber-300 rounded-md" data-testid="leave-day-details">
              <div className="text-xs font-black uppercase tracking-widest text-amber-800 mb-1">
                {selectedKey} — {onSelectedDay.length > 0 ? `${onSelectedDay.length} personel izinli` : "İzinli personel yok"}
              </div>
              {onSelectedDay.length > 0 && (
                <ul className="text-sm space-y-1">
                  {onSelectedDay.map((l) => (
                    <li key={l.id} className="text-slate-700">• <span className="font-bold">{l.personnel_name}</span>{l.reason && <span className="text-slate-500"> — {l.reason}</span>}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div>
          {rows.length === 0 ? <div className="py-16 text-center text-slate-500">Bu ay için izin kaydı yok.</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr className="text-xs uppercase tracking-widest text-slate-500">
                  <th className="text-left py-3 px-4 font-bold">Personel</th>
                  <th className="text-left py-3 px-4 font-bold">Başlangıç</th>
                  <th className="text-left py-3 px-4 font-bold">Bitiş</th>
                  <th className="text-left py-3 px-4 font-bold">Sebep</th>
                  <th className="text-right py-3 px-4 font-bold"></th>
                </tr></thead>
                <tbody>{rows.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`leave-row-${l.id}`}>
                    <td className="py-3 px-4 font-semibold text-slate-900">{l.personnel_name}</td>
                    <td className="py-3 px-4 font-mono-num text-slate-700">{l.start_date}</td>
                    <td className="py-3 px-4 font-mono-num text-slate-700">{l.end_date}</td>
                    <td className="py-3 px-4 text-slate-600">{l.reason || "—"}</td>
                    <td className="py-3 px-4 text-right">{canEdit && <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(l.id)}><Trash2 size={14} /></Button>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OvertimeTable({ rows, canEdit, onReload }) {
  const remove = async (id) => { try { await api.delete(`/personnel/overtime/${id}`); toast.success("Silindi"); onReload(); } catch (err) { toast.error(formatApiError(err)); } };
  return rows.length === 0 ? <div className="py-16 text-center text-slate-500">Bu ay için mesai kaydı yok.</div> : (
    <table className="w-full text-sm">
      <thead className="bg-slate-50"><tr className="text-xs uppercase tracking-widest text-slate-500">
        <th className="text-left py-3 px-4 font-bold">Personel</th>
        <th className="text-left py-3 px-4 font-bold">Tarih</th>
        <th className="text-left py-3 px-4 font-bold">Başlangıç</th>
        <th className="text-left py-3 px-4 font-bold">Bitiş</th>
        <th className="text-right py-3 px-4 font-bold">Süre</th>
        <th className="text-left py-3 px-4 font-bold">Not</th>
        <th className="text-right py-3 px-4 font-bold"></th>
      </tr></thead>
      <tbody>{rows.map((o) => (
        <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ot-row-${o.id}`}>
          <td className="py-3 px-4 font-semibold text-slate-900">{o.personnel_name}</td>
          <td className="py-3 px-4 font-mono-num text-slate-700">{o.date}</td>
          <td className="py-3 px-4 font-mono-num text-slate-700">{o.start_time}</td>
          <td className="py-3 px-4 font-mono-num text-slate-700">{o.end_time}</td>
          <td className="py-3 px-4 text-right font-mono-num font-bold text-green-700">{formatNumber(o.hours)} sa</td>
          <td className="py-3 px-4 text-slate-600 max-w-[240px] truncate">{o.note || "—"}</td>
          <td className="py-3 px-4 text-right">{canEdit && <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(o.id)}><Trash2 size={14} /></Button>}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function ReportTable({ summary, personnel }) {
  return (
    <div className="p-6" data-testid="report-view">
      <div className="mb-4 flex items-center gap-2 text-sm text-slate-600">
        <TrendingUp size={16} className="text-sky-500" />
        <span>Aylık kişi bazlı mesai özeti — <span className="font-bold text-slate-900 font-mono-num">{summary.month}</span></span>
      </div>
      {summary.per_person.length === 0 ? <div className="py-8 text-center text-slate-500">Bu ay için mesai kaydı yok.</div> : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-xs uppercase tracking-widest text-slate-500">
            <th className="text-left py-3 px-4 font-bold">Personel</th>
            <th className="text-right py-3 px-4 font-bold">Mesai Sayısı</th>
            <th className="text-right py-3 px-4 font-bold">Toplam Saat</th>
          </tr></thead>
          <tbody>
            {summary.per_person.map((p) => (
              <tr key={p.personnel_id} className="border-t border-slate-100" data-testid={`report-row-${p.personnel_id}`}>
                <td className="py-3 px-4 font-semibold text-slate-900">{p.personnel_name}</td>
                <td className="py-3 px-4 text-right font-mono-num text-slate-700">{p.count}</td>
                <td className="py-3 px-4 text-right font-mono-num font-black text-green-700">{formatNumber(p.hours)} sa</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-300 bg-slate-50">
              <td className="py-3 px-4 font-black uppercase text-xs tracking-widest text-slate-900">Genel Toplam</td>
              <td></td>
              <td className="py-3 px-4 text-right font-mono-num font-black text-xl text-green-700">{formatNumber(summary.grand_total_hours)} sa</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function AddDialog({ open, onClose, personnel, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({});

  useEffect(() => { if (open) setF({}); }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      if (open === "personnel") {
        if (!f.name || !f.role) { toast.error("Ad ve rol zorunlu"); setSaving(false); return; }
        await api.post("/personnel", { name: f.name, role: f.role, phone: f.phone });
      } else if (open === "leaves") {
        if (!f.personnel_id || !f.start_date || !f.end_date) { toast.error("Personel ve tarihler zorunlu"); setSaving(false); return; }
        await api.post("/personnel/leaves", { personnel_id: f.personnel_id, start_date: f.start_date, end_date: f.end_date, reason: f.reason });
      } else if (open === "overtime") {
        if (!f.personnel_id || !f.date || !f.start_time || !f.end_time) { toast.error("Tüm alanlar zorunlu"); setSaving(false); return; }
        await api.post("/personnel/overtime", { personnel_id: f.personnel_id, date: f.date, start_time: f.start_time, end_time: f.end_time, note: f.note });
      }
      toast.success("Eklendi");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`add-${open}-dialog`}>
        <DialogHeader>
          <DialogTitle>{open === "personnel" ? "Yeni Personel" : open === "leaves" ? "İzin Kaydı Ekle" : "Mesai Kaydı Ekle"}</DialogTitle>
          <DialogDescription>Bilgileri doldurun.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {open === "personnel" && <>
            <div><Label>Ad Soyad *</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} className="border-2 border-slate-300 mt-1" data-testid="in-name" /></div>
            <div><Label>Rol/Görev *</Label><Input value={f.role || ""} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="Örn: Ustabaşı" className="border-2 border-slate-300 mt-1" data-testid="in-role" /></div>
            <div><Label>Telefon</Label><Input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className="border-2 border-slate-300 mt-1 font-mono-num" data-testid="in-phone" /></div>
          </>}
          {(open === "leaves" || open === "overtime") && (
            <div><Label>Personel *</Label>
              <Select value={f.personnel_id || ""} onValueChange={(v) => setF({ ...f, personnel_id: v })}>
                <SelectTrigger className="border-2 border-slate-300 mt-1" data-testid="in-person"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                <SelectContent>{personnel.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name} — {p.role}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          )}
          {open === "leaves" && <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlangıç *</Label><Input type="date" value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="border-2 border-slate-300 mt-1" data-testid="in-start" /></div>
              <div><Label>Bitiş *</Label><Input type="date" value={f.end_date || ""} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="border-2 border-slate-300 mt-1" data-testid="in-end" /></div>
            </div>
            <div><Label>Sebep</Label><Input value={f.reason || ""} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Örn: Yıllık izin" className="border-2 border-slate-300 mt-1" /></div>
          </>}
          {open === "overtime" && <>
            <div><Label>Tarih *</Label><Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} className="border-2 border-slate-300 mt-1" data-testid="in-date" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlangıç Saati *</Label><Input type="time" value={f.start_time || ""} onChange={(e) => setF({ ...f, start_time: e.target.value })} className="border-2 border-slate-300 mt-1" data-testid="in-start-time" /></div>
              <div><Label>Bitiş Saati *</Label><Input type="time" value={f.end_time || ""} onChange={(e) => setF({ ...f, end_time: e.target.value })} className="border-2 border-slate-300 mt-1" data-testid="in-end-time" /></div>
            </div>
            <div><Label>Not</Label><Input value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} className="border-2 border-slate-300 mt-1" /></div>
          </>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button className="bg-sky-500 hover:bg-sky-600 font-bold" onClick={submit} disabled={saving} data-testid="save-btn">
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
